import { View } from "@westpierstudio/luma/mvc";
import { ElasticCache, ExternalItem } from "@westpierstudio/luma/utils/elastic-cache";
import { GameServices } from "game/GameServices";
import { OverlayID, SymbolID } from "servertypes/SharedTypes";
import { Signal } from "type-signals";
import { SymbolCache } from "./Reels";
import { Tween } from "@westpierstudio/luma/createjs";
import { setSpineSkins, addToSpineSlot, clearSpineSlot } from "utils/Misc";
import { Spine } from "pixi-spine";
import { Container } from "pixi.js";

class ReelSymbolSignals {
    scatterWinComplete = new Signal();
    dragonRoar = new Signal<(inAnimLoop: boolean) => void>();
    pandaRoar = new Signal<(inAnimLoop: boolean) => void>();
}

export class ReelSymbol extends View {
    private symbol?: ExternalItem<Spine & Container>;
    private overlay?: ExternalItem<Spine & Container>;
    private overlayFrame?: ExternalItem<Spine & Container>;
    private symbolContainer: Container;
    private overlayContainer: Container;
    private _alteredWild = false;
    private overlayAnimState = false;
    private mSymbolAnimLoop = false;
    private mSymbolInteractAnim = false;
    private mOverlayScatterWinAnim = false;
    private mRequestOverlayAnimLoop = false;
    _id: SymbolID = `F9`;
    _overlayID: OverlayID = `E`;
    signals = new ReelSymbolSignals();

    constructor(protected override readonly services: GameServices, private readonly symbolCache: SymbolCache,
        private readonly overlayCache: ElasticCache<Spine & Container>, private readonly overlaysResetMap: Map<number, ExternalItem<Spine & Container>>,
        private readonly overlayFrameCache: ElasticCache<Spine & Container>, private readonly overlaysFrameResetMap: Map<number, ExternalItem<Spine & Container>>) {
        super(services);

        const {factory} = this.services;

        this.symbolContainer = factory.createContainer({}, this);
        this.overlayContainer = factory.createContainer({}, this);

        this.addListener(`click`, this.onInteract, this)
        .addListener(`tap`, this.onInteract, this);
    }

    get id() {
        return this._id;
    }

    get overlayID() {
        return this._overlayID;
    }

    get alteredWild() {
        return this._alteredWild;
    }

    setSymbol(id: SymbolID, overlayID: OverlayID, alteredWildState: boolean, alteredWildAnim: boolean = true): void {

        if (this.symbol) {

            this.symbolContainer.removeChild(this.symbol.reference);
            this.symbolCache[this._id].free(this.symbol);
        }

        this._id = id;
        this.symbol = this.symbolCache[this._id].get();
        this.symbolContainer.addChildAt(this.symbol.reference, 0);

        // RESET SYMBOL SCALe
        this.symbol.reference.scale = {x: 1, y: 1};

        setSpineSkins(this.symbol.reference, [`default`]);

        this._alteredWild = alteredWildState;
        this.mSymbolAnimLoop = false;

        this.setOverlay(overlayID);

        this.setAnimStaticState(alteredWildAnim);

        this.interactive = this.buttonMode = this.overlayID === `E` && (this._id === `M1` || this._id === `M2`);
    }

    showOverlay(): void {

        if (this.overlay && this.overlayContainer.children.length > 0) {
            this.overlay.reference.x = 0;
            this.overlay.reference.y = 0;
            this.overlayContainer.addChildAt(this.overlay.reference, 1);
        }
    }

    setToAlteredWild(multiplier?: number): void {

        //Do not show altered wilds for symbols with overlays present
        if (this.overlayID === `E`) {

            this._alteredWild = true;
            this.setSymbol(`WW`, `E`, true);

            if (multiplier && this.symbol) {

                setSpineSkins(this.symbol.reference, [`${multiplier}x`]);
                this.symbol.reference.state.setAnimation(1, `multiplier-IN`, false);
            }
        }
    }

    startWinAnimLoop(): void {

        if (this.mOverlayScatterWinAnim) {
            this.mRequestOverlayAnimLoop = true;

            return;
        }

        this.clearOverlayAnims();

        if (this._id === `WW`) {
            this.symbol?.reference.state.clearTrack(0);
            this.wildWinAnimLoop();
        } else if (this._id[0] === `M`) {
            this.mSymbolAnimLoop = true;
            this.symbol?.reference.state.setAnimation(0, `win`, true);
        }

        this.overlayAnimLoop();
    }

    wildWinAnimLoop(): void {

        if (this.alteredWild) {
            this.symbol?.reference.state.setAnimation(0, `ww-gold-loop`, true);
            this.symbol?.reference.state.addListener( { complete: (entry) => {

                if (entry.trackIndex === 0) {

                    this.symbol?.reference.state.setAnimation(1, `multiplier-LOOP`, false);
                }
            }});
        } else {
            this.symbol?.reference.state.setAnimation(0, `ww-loop`, true);
        }
    }

    overlayAnimLoop(): void {

        let anim;
        if (this.overlayAnimState) {
            anim = `overlay-out`;
        } else {
            anim = `overlay-in`;
        }

        this.overlay?.reference.state.setAnimation(2, anim, false);
        this.overlay?.reference.state.addListener({ complete: (entry) => {

            if (entry.trackIndex === 2) {

                this.overlay?.reference.state.clearListeners();
                this.overlayAnimState = !this.overlayAnimState;

                this.overlayAnimLoop();
            }
        }});
    }

    stopWinAnimLoop(): void {

        if (this._id === `WW`) {
            if (this.alteredWild) {
                this.symbol?.reference.state.setAnimation(0, `static-gold`, true);
            } else {
                this.symbol?.reference.state.setAnimation(0, `static-red`, true);
            }
        } else if (this._id[0] === `M`)
        {
            this.mSymbolAnimLoop = false;
            this.symbol?.reference.state.setAnimation(0, `static`, true);
        }

        this.overlay?.reference.state.setAnimation(2, `static`, true);
    }

    bonusWinAnims(): void {

        this.clearOverlayAnims();

        this.overlay?.reference.state.setAnimation(1, `win`, false);

        if (this.overlayID === `FG` || this.overlayID === `VS`) {

            this.overlay?.reference.state.setAnimation(3, `initiate`, false);
        }
    }

    scatterWinSequence(): void {

        Tween.get(this)
        .wait(3000)
        .call(() => {
            this.signals.scatterWinComplete.dispatch();
        });
    }

    startScatterWinLoop(complete: boolean = true): void {

        this.clearOverlayAnims();

        this.mOverlayScatterWinAnim = true;

        this.overlay?.reference.state.setAnimation(1, `win`, true);
        this.overlay?.reference.state.addListener({ complete: (entry) => {

            if (entry.trackIndex === 1 && this.mRequestOverlayAnimLoop) {

                this.mOverlayScatterWinAnim = false;
                this.startWinAnimLoop();
            }
        }});

        if (complete && (this.overlayID === `FG` || this.overlayID === `VS`)) {

            this.overlay?.reference.state.setAnimation(3, `complete-loop`, true);
        }

        Tween.get(this)
        .wait(3000)
        .call(() => {
            this.signals.scatterWinComplete.dispatch();
        });
    }

    playLandAnim(): void {

        if (this.overlay) {

            this.clearOverlayAnims();

            this.overlay.reference.state.setAnimation(0, `land`, false);
        }
    }

    setInteractive(interactive: boolean) {
        this.interactive = this.buttonMode = interactive;
    }

    getOverlay(): ExternalItem<Spine & Container> | undefined {
        return this.overlay;
    }

    override reset(): void {
        this._alteredWild = this.mSymbolAnimLoop = this.mSymbolInteractAnim = false;
    }

    private setOverlay(overlayID: OverlayID): void {

        if (overlayID !== `E`) {

            this.removeOverlay();

            this._overlayID = overlayID;

            this.overlayFrame = this.overlayFrameCache.get();
            this.overlayContainer.addChildAt(this.overlayFrame.reference, 0);
            this.overlaysFrameResetMap.set(this.overlayFrame.id, this.overlayFrame);
            this.overlayFrame?.reference.state.setAnimation(0, `frame-loop`, true);

            this.overlay = this.overlayCache.get();
            this.overlayContainer.addChildAt(this.overlay.reference, 1);
            this.overlaysResetMap.set(this.overlay.id, this.overlay);

            if (this.symbol) {
                clearSpineSlot(this.overlay.reference, `dynamic-slot`);
                addToSpineSlot(this.overlay.reference, this.symbol?.reference, `dynamic-slot`);

                const scaleMultiplier: number = this.services.resources.getSetting(`coinSymbolScaleMultiplier`);
                this.symbol.reference.scale.x = this.symbol.reference.scale.x * scaleMultiplier;
                this.symbol.reference.scale.y = this.symbol.reference.scale.y * scaleMultiplier;
            }

            this.symbolContainer.visible = false;

            let overlayAssigned = false;
            const symbolOverlayLabels: Record<string, string> = this.services.resources.getSetting(`symbolOverlayLabels`);
            for (const [key, src] of Object.entries(symbolOverlayLabels)) {
                if (overlayID == key) {
                    setSpineSkins(this.overlay.reference, [src]);
                    overlayAssigned = true;
                }
            }
            if (!overlayAssigned) {
                setSpineSkins(this.overlay.reference, [`blank`]);
            }

        } else {
            this.removeOverlay();
        }
    }

    private removeOverlay(): void {

        if (this.overlay && this.overlayFrame && this._overlayID !== `E`) {

            this.overlaysFrameResetMap.delete(this.overlayFrame.id);
            this.overlayContainer.removeChild(this.overlayFrame.reference);
            this.overlayFrameCache.free(this.overlayFrame);

            this.overlaysResetMap.delete(this.overlay.id);
            this.overlayContainer.removeChild(this.overlay.reference);
            this.overlayCache.free(this.overlay);

            this.overlayFrame = undefined;
            this.overlay = undefined;
            this._overlayID = `E`;

            this.symbolContainer.visible = true;

            this.mOverlayScatterWinAnim = false;
            this.mRequestOverlayAnimLoop = false;
        }
    }

    private setAnimStaticState(alteredWildAnim: boolean): void {

        this.symbol?.reference.state.clearTracks();
        this.symbol?.reference.state.clearListeners();

        if (this.overlayID !== `E`) {

            this.overlay?.reference.state.clearTracks();

            this.overlay?.reference.state.setAnimation(0, `static`, true);
        } if (this._id === `WW`) {

            if (this._alteredWild && alteredWildAnim) {
                this.doAlteredWildAnim();
            } else if (this._alteredWild) {
                this.symbol?.reference.state.setAnimation(0, `static-gold`, true);
            } else {
                this.symbol?.reference.state.setAnimation(0, `static-red`, true);
            }

        } else if (this._id[0] === `M`) {
            this.mSymbolAnimLoop = false;
            this.symbol?.reference.state.setAnimation(0, `static`, true);
        }
    }

    private doAlteredWildAnim(): void {
        this.symbol?.reference.state.setAnimation(0, `ww-gold-glow`, false);
        this.symbol?.reference.state.setAnimation(0, `static-gold`, true);
        this.symbol?.reference.state.setAnimation(2, `ww-hit-explosion`, false);
    }

    private onInteract(): void {
        if (this.overlayID !== `E`) {
            return;
        }

        if (!this.mSymbolAnimLoop && !this.mSymbolInteractAnim) {

            this.mSymbolInteractAnim = true;

            if (this._id === `M1`) {
                this.symbol?.reference.state.setAnimation(0, `win`, false);
                this.signals.dragonRoar.dispatch(false);
            } else if (this._id === `M2`) {
                this.symbol?.reference.state.setAnimation(0, `win`, false);
                this.signals.pandaRoar.dispatch(false);
            }

            this.symbol?.reference.state.addListener({ complete: (entry) => {
                if (entry.trackIndex === 0) { this.mSymbolInteractAnim = false; }
            }});
        } else if (!this.mSymbolInteractAnim) {

            if (this._id === `M1`) {
                this.signals.dragonRoar.dispatch(true);
            } else if (this._id === `M2`) {
                this.signals.pandaRoar.dispatch(true);
            }
        }
    }

    private clearOverlayAnims(): void {

        this.overlay?.reference.state.clearTracks();
        this.overlay?.reference.state.clearListeners();

        this.mOverlayScatterWinAnim = false;
        this.mRequestOverlayAnimLoop = false;
    }
}